import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { catchError, Observable, throwError } from 'rxjs';
import {
	agreementRequest,
	Asset,
	BankInfo,
	cancelRedemptionRequest,
	createIssuanceRequest,
	Fund,
	fundLoadOptions,
	issuanceResponse,
	navHistory,
	navHistoryFilter,
	redemptionRequest
} from '../models/funds.model';
import { BaseService } from '@easy-lib/modules/shared/services';
import { apiUrls } from '@easy-lib/constants';

@Injectable({
	providedIn: 'root'
})
export class FundsService extends BaseService {
	funds(): Observable<Fund[]> {
		return this.httpService.get<Fund[]>(this.mtsPath + apiUrls.fund + 'funds/');
	}

	assets(): Observable<Asset[]> {
		return this.httpService.get<Asset[]>(this.mtsPath + apiUrls.fund + 'assets/');
	}
	banksInfo(): Observable<BankInfo[]> {
		return this.httpService.get<any[]>(this.mtsPath + apiUrls.fund + 'bank-info/');
	}
	transactions(fundCode = '', loadOption: fundLoadOptions): Observable<any> {
		let queryParams = new HttpParams();
		queryParams = queryParams.append('page', loadOption.page);
		queryParams = queryParams.append('size', loadOption.size);
		return this.httpService.get(this.mtsPath + apiUrls.fund + 'transactions/' + fundCode, {
			options: { params: queryParams }
		});
	}

	issuanceTransactions(fundCode = '', loadOption: fundLoadOptions) {
		let queryParams = new HttpParams();
		queryParams = queryParams.append('page', loadOption.page);
		queryParams = queryParams.append('size', loadOption.size);
		return this.httpService.get(this.mtsPath + apiUrls.fund + 'issuanceTransactions/' + fundCode, {
			options: { params: queryParams }
		});
	}

	allTransactions(fundCode = '', loadOption: fundLoadOptions, nationalCode?: string | number) {
		let queryParams = new HttpParams();
		queryParams = queryParams.append('page', loadOption.page.toString());
		queryParams = queryParams.append('size', loadOption.size.toString());
		if (fundCode !== '') {
			queryParams = queryParams.append('fundCode', fundCode);
			queryParams = queryParams.append('nationalCode', nationalCode);
		}
		return this.httpService.get(this.mtsPath + apiUrls.fund + 'allTransactions/', {
			options: { params: queryParams }
		});
	}

	redemptionTransactions(fundCode = '', loadOption: fundLoadOptions) {
		let queryParams = new HttpParams();
		queryParams = queryParams.append('page', loadOption.page);
		queryParams = queryParams.append('size', loadOption.size);
		return this.httpService.get(this.mtsPath + apiUrls.fund + 'redemptionTransactions/' + fundCode, {
			options: { params: queryParams }
		});
	}

	indexAverage(days: number): Observable<number> {
		return this.httpService.post<number>(this.mtsPath + apiUrls.easy + 'marketView/IndexAverageForDateRange', {
			body: { days }
		});
	}

	navHistory(filter: navHistoryFilter): Observable<navHistory[]> {
		let queryParams = new HttpParams();
		queryParams = queryParams.append('fromDate', filter.fromDate);
		queryParams = queryParams.append('toDate', filter.toDate);
		return this.httpService.get<navHistory[]>(`${this.mtsPath + apiUrls.fund}fund/${filter.fundCode}/nav-history`, {
			options: { params: queryParams }
		});
	}

	issuance(req: createIssuanceRequest): Observable<issuanceResponse> {
		return this.httpService.post<issuanceResponse>(this.mtsPath + apiUrls.fund + 'issuance', { body: req });
	}

	cancel(requestId: string): Observable<issuanceResponse> {
		return this.httpService.post<issuanceResponse>(this.mtsPath + apiUrls.fund + 'cancel', {
			body: { requestId }
		});
	}

	cancelRedemption(req: cancelRedemptionRequest): Observable<issuanceResponse> {
		return this.httpService.post<issuanceResponse>(this.mtsPath + apiUrls.fund + 'cancelRedemption', {
			body: req
		});
	}

	redeem(req: redemptionRequest): Observable<issuanceResponse> {
		return this.httpService
			.post<issuanceResponse>(this.mtsPath + apiUrls.fund + 'redeem', {
				body: req
			})
			.pipe(catchError(error => throwError(() => error)));
	}

	agreement(req: agreementRequest): Observable<any> {
		return this.httpService.post<any>(this.mtsPath + apiUrls.fund + 'agreement', { body: req });
	}

	consultationRequest(): Observable<any> {
		return this.httpService.post<any>(this.mtsPath + apiUrls.crm + 'consultation-request', { body: {} });
	}
}
